import React, { useState } from 'react';
import { isLoading } from 'resift';

// Components
import Button from '@mui/material/Button';
import AppBarPortal from 'pages/Onboarding/components/AppBarPortal';
import AttributeAccordion from 'pages/Onboarding/components/AttributeAccordion';
import ContrastButton from 'pages/Onboarding/components/ContrastButton';
import Header from 'pages/Onboarding/components/Header';
import SkipDialog from 'pages/Onboarding/components/SkipDialog';
import { Prompt } from 'routerCompat';

import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material/styles';

const useStyles = makeStyles()((theme: Theme) => ({
  root: {},
  inputContainer: {
    marginBottom: theme.spacing(4),
  },
  skipButton: {
    marginRight: theme.spacing(2),
  },
  buttonDisabled: {
    color: `${theme.palette.grey['300']} !important`,
    backgroundColor: `${theme.palette.grey['600']} !important`,
  },
}));

interface Props {
  displayName: string;
  description: string;
  input: React.ReactNode;
  isValid: boolean;
  fromLinkedIn: boolean;
  isLastSlide: boolean;
  status: number;
  onContinue: () => void;
  onSkip: () => void;
  showSkipConfirmation: boolean;
  showNavigationConfirmation: boolean;
}

function FieldTemplate({
  onContinue,
  isLastSlide,
  isValid,
  status,
  input,
  displayName,
  description,
  onSkip,
  fromLinkedIn,
  showSkipConfirmation,
  showNavigationConfirmation,
}: Props) {
  const { classes } = useStyles();
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);

  const handleSkip = () => {
    if (showSkipConfirmation) {
      setSkipDialogOpen(true);
    } else {
      onSkip();
    }
  };

  const handleSkipDialogClose = () => {
    setSkipDialogOpen(false);
  };

  return (
    <>
      <AppBarPortal>
        <ContrastButton onClick={handleSkip} className={classes.skipButton}>
          Skip
        </ContrastButton>
        <Button
          variant="contained"
          color="secondary"
          onClick={onContinue}
          disabled={!isValid || isLoading(status)}
          classes={{
            disabled: classes.buttonDisabled,
          }}
        >
          {isLastSlide ? 'Finish' : 'Continue'}
        </Button>
      </AppBarPortal>
      <Prompt
        when={showNavigationConfirmation && !skipDialogOpen}
        message="Are you sure you'd like to leave? Your changes have not been saved yet."
      />
      <SkipDialog open={skipDialogOpen} onClose={handleSkipDialogClose} onSkip={onSkip} />

      <div className={classes.root}>
        <Header displayName={displayName} description={description} fromLinkedIn={fromLinkedIn} />
        <div className={classes.inputContainer}>{input}</div>
        <AttributeAccordion />
      </div>
    </>
  );
}

export default FieldTemplate;
